
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { getAllPosts } from "@/lib/contentQueries";
import { BsArrowRight } from "react-icons/bs";
import { FaBookOpen, FaChessKnight } from "react-icons/fa";
import Head from "next/head";
import Image from "next/image";
import PageHeader from "../../components/layout/PageHeader";
import PageShell from "../../components/layout/PageShell";
import { client } from "../../../tina/__generated__/client";
import Link from "next/link";
import { getPostHref, getTopicHref } from "@/lib/contentRouting";
import StaticRedirect from "@/components/navigation/StaticRedirect";
function getTextContent(node: any): string {
    if (!node)
        return "";
    if (typeof node.text === "string")
        return node.text;
    if (!Array.isArray(node.children))
        return "";
    return node.children.map(getTextContent).join(" ");
}
function getGuidePreview(post: any): string {
    const excerpt = typeof post.excerpt === "string"
        ? post.excerpt
        : getTextContent(post.excerpt);
    if (excerpt.trim())
        return excerpt.replace(/\s+/g, " ").trim();
    const subtitle = typeof post.subtitle === "string"
        ? post.subtitle
        : getTextContent(post.subtitle);
    if (subtitle.trim())
        return subtitle.replace(/\s+/g, " ").trim();
    const children = post?._body?.children;
    if (!Array.isArray(children))
        return "";
    const paragraph = children.find((child) => child?.type === "p");
    return getTextContent(paragraph).replace(/\s+/g, " ").trim();
}
function getGuideTimestamp(postData: any): number {
    const timestamp = new Date(postData?.node?.date || "").getTime();
    return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
}
function GuideArtwork({ post, priority = false }) {
    const image = post.cardImg || post.heroImg;
    if (image) {
        return (<Image src={image} alt="" fill priority={priority} sizes={priority
                ? "(min-width: 768px) 50vw, 100vw"
                : "(min-width: 768px) 33vw, 100vw"} className="object-cover transition-transform duration-500 group-hover:scale-[1.025]"/>);
    }
    return (<div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0b2b4d] via-[#123f70] to-altblue-600">
      <FaChessKnight aria-hidden="true" className="h-16 w-16 text-white/25"/>
    </div>);
}
function GuideCard({ post }: {
    post: any;
}) {
    const preview = getGuidePreview(post);
    const categoryLabel = post?.category?.category?.trim();
    return (<article className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
      <Link href={getPostHref(post)} locale={false} className="flex h-full flex-col">
        <div className="relative aspect-[16/9] overflow-hidden bg-slate-800">
          <GuideArtwork post={post}/>
        </div>
        <div className="flex flex-1 flex-col p-5 sm:p-6">
          {categoryLabel && (<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-altblue-600">
              {categoryLabel}
            </p>)}
          <h3 className="text-xl font-bold leading-snug tracking-tight text-slate-900 group-hover:text-altblue-700">
            {post.title}
          </h3>
          {preview && (<p className="mt-3 text-sm leading-6 text-slate-600 line-clamp-3">
              {preview}
            </p>)}
          <span className="mt-auto inline-flex items-center gap-2 pt-5 text-sm font-bold text-slate-700 group-hover:text-altblue-700">
            Read guide
            <BsArrowRight aria-hidden="true" className="transition-transform group-hover:translate-x-1"/>
          </span>
        </div>
      </Link>
    </article>);
}
export default function Guides(props: any) {
    if (props.redirectTo) {
        return <StaticRedirect destination={props.redirectTo}/>;
    }
    const sortedPosts = [...(props.posts || [])].sort((left, right) => getGuideTimestamp(right) - getGuideTimestamp(left));
    const [featuredPostData, ...remainingPostData] = sortedPosts;
    const featuredPost = featuredPostData?.node;
    const featuredPreview = featuredPost ? getGuidePreview(featuredPost) : "";
    const featuredCategory = featuredPost?.category?.category?.trim();
    return (<>
      <Head>
        <title>
          Chess Guides – Openings, Strategy and Improvement | Chess Hotel
        </title>
        <meta name="description" content="Practical chess guides on openings, tactics, strategy, and improvement. Learn the ideas, then practise them on Chess Hotel."/>
        <meta property="og:title" content="Chess Guides | Chess Hotel"/>
        <meta property="og:description" content="Practical chess guides that help you understand ideas and make better decisions over the board."/>
      </Head>

      <PageShell background="light">
        <PageHeader title="Chess guides for better decisions" description="Clear, practical lessons on openings, tactics, strategy, and the habits that help you improve." icon={FaBookOpen}/>

        {props.topics.length > 0 && (<nav aria-label="Guide topics" className="mb-10">
            <div className="flex items-center gap-3">
              <h2 className="shrink-0 text-sm font-bold text-slate-900">
                Browse by topic
              </h2>
              <div className="h-px flex-1 bg-slate-200"/>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {props.topics.map((topic) => (<Link key={topic.slug} href={getTopicHref(topic.slug)} locale={false} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-altblue-300 hover:text-altblue-700">
                  {topic.label}
                  <span className="text-xs font-medium text-slate-400">
                    {topic.count}
                  </span>
                </Link>))}
            </div>
          </nav>)}

        {featuredPost ? (<>
            <section aria-labelledby="featured-guide-heading">
              <div className="mb-5 flex items-center gap-3">
                <h2 id="featured-guide-heading" className="shrink-0 text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">
                  Featured guide
                </h2>
                <div className="h-px flex-1 bg-slate-200"/>
              </div>
              <article className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg">
                <Link href={getPostHref(featuredPost)} locale={false} className="grid md:grid-cols-[1.12fr_0.88fr]">
                  <div className="relative aspect-[16/9] overflow-hidden bg-slate-800 md:aspect-auto md:min-h-[360px]">
                    <GuideArtwork post={featuredPost} priority/>
                  </div>
                  <div className="flex flex-col justify-center p-7 sm:p-9 md:p-10">
                    {featuredCategory && (<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-altblue-600">
                        {featuredCategory}
                      </p>)}
                    <h3 className="text-2xl font-black leading-tight tracking-tight text-slate-900 group-hover:text-altblue-700 sm:text-3xl">
                      {featuredPost.title}
                    </h3>
                    {featuredPreview && (<p className="mt-4 text-base leading-7 text-slate-600 line-clamp-4">
                        {featuredPreview}
                      </p>)}
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-slate-800 group-hover:text-altblue-700">
                      Read the guide
                      <BsArrowRight aria-hidden="true" className="transition-transform group-hover:translate-x-1"/>
                    </span>
                  </div>
                </Link>
              </article>
            </section>

            {remainingPostData.length > 0 && (<section className="pb-12 pt-10" aria-labelledby="latest-guides-heading">
                <div className="mb-5 flex items-center gap-3">
                  <h2 id="latest-guides-heading" className="shrink-0 text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">
                    More guides
                  </h2>
                  <div className="h-px flex-1 bg-slate-200"/>
                </div>
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {remainingPostData.map(({ node: post }) => (<GuideCard key={post._sys.filename} post={post}/>))}
                </div>
              </section>)}
          </>) : (<div className="rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center text-slate-600">
            New chess guides are coming soon.
          </div>)}
      </PageShell>
    </>);
}
const getStaticProps = async ({ locale }) => {
    if (locale && locale !== "en") {
        return {
            props: { redirectTo: "/guides" },
        };
    }
    const postsResponse = await getAllPosts({
        filter: { locale: { eq: "en" } },
    });
    const posts = postsResponse.data.postConnection.edges.filter((edge) => {
        const post = edge?.node;
        const categorySlug = post?.category?.slug;
        const folderCategory = post?._sys?.breadcrumbs?.[0];
        return categorySlug !== "news" && folderCategory !== "news";
    });
    const topicsBySlug = new Map();
    posts.forEach((edge) => {
        const category = edge?.node?.category;
        if (category?.slug && !topicsBySlug.has(category.slug)) {
            topicsBySlug.set(category.slug, {
                label: category.category,
                slug: category.slug,
                count: 1,
            });
        }
        else if (category?.slug) {
            topicsBySlug.get(category.slug).count += 1;
        }
    });
    const topics = Array.from(topicsBySlug.values()).sort((left: any, right: any) => right.count - left.count || left.label.localeCompare(right.label));
    return {
        props: {
            posts,
            topics,
        },
    };
};

    async function __Next_Translate__getStaticProps__1a0fc0d5427__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/guides/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0fc0d5427__ as getStaticProps }
  